import { faPlusCircle, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import {
  getSupplierGetSupplierQueryKey,
  useSupplierAddCompanyToSupplier,
  useSupplierGetSupplier,
  useSupplierRemoveCompanyFromSupplier,
} from 'api/supplier/supplier';
import Button from 'components/inputs/Button';
import { RequestStatus } from 'hooks/useApiCallFn';
import useCompanies, {
  useRefreshCompaniesCall,
} from 'contexts/basicData/useCompanies';
import { FC, useMemo } from 'react';
import styled from 'styled-components';
import Widget from 'components/Dashboard/Widget';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 15px;
  min-width: 400px;
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;

  & > :first-child {
    flex: 0 0 110px; /* Fixed width of 110px for the button, to avoid text to shrink it */
  }

  & > :last-child {
    flex: 1;
  }
`;

interface Props {
  supplierId: number;
}

const ConnectCompaniesAndSupplier: FC<Props> = ({ supplierId }) => {
  const queryClient = useQueryClient();
  const supplier = useSupplierGetSupplier({
    id: supplierId,
  });
  const addCompanyToSupplier = useSupplierAddCompanyToSupplier();
  const removeCompanyFromSupplier = useSupplierRemoveCompanyFromSupplier();
  const companies = useCompanies();
  const refreshCompaniesCall = useRefreshCompaniesCall();

  const refreshState = async () => {
    await refreshCompaniesCall.run();
    await queryClient.invalidateQueries(
      getSupplierGetSupplierQueryKey({
        id: supplierId,
      })
    );
  };

  const isLoadingCalls = useMemo(() => {
    return (
      supplier.isLoading ||
      addCompanyToSupplier.isLoading ||
      removeCompanyFromSupplier.isLoading ||
      refreshCompaniesCall.status === RequestStatus.Fetching
    );
  }, [
    supplier.isLoading,
    addCompanyToSupplier.isLoading,
    removeCompanyFromSupplier.isLoading,
    refreshCompaniesCall.status,
  ]);

  return (
    <ContentWrapper>
      <Widget title={`Kopplade företag för ${supplier.data?.name}`}>
        <ListWrapper>
          {supplier.data?.companies.map((company) => (
            <Row key={company.companyID}>
              <Button
                iconPlacement="right"
                icon={<FontAwesomeIcon icon={faXmark} />}
                disabled={isLoadingCalls}
                onClick={async () => {
                  await removeCompanyFromSupplier.mutateAsync({
                    params: {
                      supplierId,
                      companyId: company.companyID,
                    },
                  });
                  await refreshState();
                }}
              >
                Ta bort
              </Button>
              <span>
                <strong>{company.name}</strong>
                <br />
                <span>
                  {company.address}, {company.zip}, {company.city}
                </span>
              </span>
            </Row>
          ))}
          {supplier.data?.companies.length === 0 && 'Inga kopplade företag'}
        </ListWrapper>
      </Widget>
      <Widget
        title="Okopplade företag"
        info="Företag som inte är kopplade till någon leverantör, företag kan endast kopplas till en leverantör."
      >
        <ListWrapper>
          {companies.companies.map((company) => (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {company.supplierID === null && (
                <Row key={`Okopplad_${company.companyID}`}>
                  <Button
                    disabled={isLoadingCalls}
                    iconPlacement="right"
                    icon={<FontAwesomeIcon icon={faPlusCircle} />}
                    onClick={async () => {
                      await addCompanyToSupplier.mutateAsync({
                        params: {
                          supplierId,
                          companyId: company.companyID,
                        },
                      });
                      await refreshState();
                    }}
                  >
                    Koppla
                  </Button>
                  <span>
                    <strong>{company.name}</strong>
                    <br />
                    <span>
                      {company.address}, {company.zip}, {company.city}
                    </span>
                  </span>
                </Row>
              )}
            </>
          ))}
        </ListWrapper>
      </Widget>
    </ContentWrapper>
  );
};

export default ConnectCompaniesAndSupplier;
